import React, { useMemo, useState, useEffect } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import ProfessionalFALDocument from '../pdf/ProfessionalFALDocument';
import ComprehensiveFALDocument from '../pdf/ComprehensiveFALDocument';
import { generateFALPDF } from '../../utils/pdfUtils';

// Local error boundary to catch PDFViewer crashes
class PDFErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    console.error('PDFViewer crashed (FAL):', error, info);
    if (typeof this.props.onCrash === 'function') this.props.onCrash(error);
  }
  render() { return this.state.hasError ? null : this.props.children; }
}

// Modal to preview Final Approval Letter (FAL) exactly like KYP preview UX
const FALPreviewModal = ({ isOpen, onClose, application, remarks = '', canDownload = true, leadGeneratorData, template = 'comprehensive' }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [renderError, setRenderError] = useState(null);

  // Freeze inputs on open to prevent continuous re-renders from changing prop identities
  const deepClone = (obj) => {
    if (!obj) return obj;
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (e) {
      // Fallback to original reference if cloning fails (e.g., circular refs, Firestore timestamps)
      return obj;
    }
  };
  const [frozenApplication, setFrozenApplication] = useState(null);
  const [frozenRemarks, setFrozenRemarks] = useState('');
  const [frozenLeadGeneratorData, setFrozenLeadGeneratorData] = useState(null);
  useEffect(() => {
    if (!isOpen) return;
  const safeApp = deepClone(application) || application || {};
  setFrozenApplication(safeApp);
  setFrozenRemarks(remarks || '');
  setFrozenLeadGeneratorData(deepClone(leadGeneratorData) || leadGeneratorData || null);
  }, [isOpen]);

  // Cleanup blob URL on close/unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Memoize the PDF component to prevent re-renders
  const effectiveApplication = frozenApplication || application || {};
  const effectiveRemarks = (frozenRemarks ?? remarks) || '';
  const effectiveLeadGen = frozenLeadGeneratorData || leadGeneratorData || null;

  const pdfComponent = useMemo(() => (
    template === 'comprehensive'
      ? <ComprehensiveFALDocument application={effectiveApplication} remarks={effectiveRemarks} leadGeneratorData={effectiveLeadGen} />
      : <ProfessionalFALDocument application={effectiveApplication} remarks={effectiveRemarks} leadGeneratorData={effectiveLeadGen} />
  ), [effectiveApplication, effectiveRemarks, effectiveLeadGen, template]);

  const onCrash = async (error) => {
    setRenderError(error?.message || String(error));
    setFallbackMode(true);
    try {
      const appForPdf = frozenApplication || application || {};
      const result = await generateFALPDF(appForPdf, { remarks: effectiveRemarks, template });
      if (result?.success && result.blob) {
        const url = URL.createObjectURL(result.blob);
        setPreviewUrl(url);
      }
    } catch (e) {
      console.error('Fallback FAL PDF blob generation failed:', e);
    }
  };

  const downloadPreviewPDF = async () => {
    try {
      setIsGenerating(true);
  const appForPdf = frozenApplication || application || {};
  const result = await generateFALPDF(appForPdf, { remarks: effectiveRemarks, template });
      if (!result?.success) throw new Error(result?.error || 'Failed to generate FAL PDF');
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.fileName || `FAL_Official_${frozenApplication?.applicationId || Date.now()}_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('FAL PDF download error:', e);
      alert('Failed to generate FAL PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
      zIndex: 1000, padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '10px', padding: '20px', maxWidth: '95vw', maxHeight: '95vh',
        overflow: 'auto', position: 'relative', width: 'fit-content'
      }}>
        {/* Header with close and download buttons */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px',
          borderBottom: '2px solid #1e4d72', paddingBottom: '10px'
        }}>
          <h2 style={{ color: '#1e4d72', margin: 0 }}>Final Approval Letter (FAL)</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={downloadPreviewPDF}
              disabled={isGenerating || !canDownload}
              style={{
                backgroundColor: isGenerating || !canDownload ? '#94a3b8' : '#0ea5e9',
                color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: (isGenerating || !canDownload) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold'
              }}
            >
              {isGenerating ? '⏳ Building...' : '📄 Download FAL PDF'}
            </button>
            <button
              onClick={onClose}
              style={{
                backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold'
              }}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Embedded FAL PDF viewer with safe fallback */}
        {!fallbackMode ? (
          <div style={{ width: '80vw', height: '80vh', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
            <PDFErrorBoundary onCrash={onCrash}>
              <PDFViewer width="100%" height="100%" showToolbar={false}>
                {pdfComponent}
              </PDFViewer>
            </PDFErrorBoundary>
          </div>
        ) : (
          <div style={{ width: '80vw', height: '80vh', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '8px', background: '#fffbe6', color: '#8a6d3b', fontSize: '13px' }}>
              PDF live preview fell back due to a viewer error{renderError ? `: ${renderError}` : ''}. Using safe preview.
            </div>
            {previewUrl ? (
              <iframe title="FAL Preview" src={`${previewUrl}#toolbar=0&navpanes=0`} style={{ border: 'none', width: '100%', height: '100%' }} />
            ) : (
              <div style={{ padding: '16px', fontSize: '14px' }}>Preparing preview…</div>
            )}
          </div>
        )}

        {/* Footer Instructions */}
        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px', fontSize: '14px', color: '#666' }}>
          <strong>📋 Instructions:</strong> Review the Final Approval Letter. This summarizes discharge review and settlement. {canDownload ? 'Click "Download FAL PDF" to save the document.' : 'Download will be enabled after you approve the FAL.'}
        </div>
      </div>
    </div>
  );
};

export default FALPreviewModal;
